import { BellRing, Mail, Monitor, Settings, Smartphone } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../../lib/useMediaQuery.ts';
import { SETTINGS_SECTIONS } from '../../settings/registry.ts';
import { PushControl } from '../../notify/PushControl.tsx';
import { useNotifyState } from '../../notify/api.ts';
import { loadProbes, useOnboarding } from '../client.ts';
import type { NotifyProbe } from '../model.ts';
import { Callout } from '../parts.tsx';
import { PhoneStep } from './PhoneStep.tsx';

type Choice = 'phone' | 'browser' | 'team';

/** What reaches the person now: the channels the notify module reports, or what is missing. Without store reads: rendered in tests. */
export function ChannelSummary({ notify }: { notify: NotifyProbe | null }) {
  if (!notify) return null;
  // Without the notify module there is nothing to report yet: the phone and browser tabs below say what to set up.
  if (!notify.installed) return null;
  const n = notify.channels.length;
  if (n) {
    return (
      <Callout tone="ok" title={`Requests reach you on ${n === 1 ? 'one channel' : `${n} channels`}`}>
        {notify.channels.join(', ')}.
      </Callout>
    );
  }
  return (
    <p className="flex items-center gap-2 text-caption text-fg-subtle" role="status" aria-live="polite">
      <BellRing className="size-3.5" aria-hidden />
      No channel yet. Pick at least one; this updates by itself.
    </p>
  );
}

/** Step 5: at least one notification channel, phone push first, then this browser, then Slack, Teams or email (R-HL-2). */
export function ChannelStep() {
  const navigate = useNavigate();
  const notify = useOnboarding((s) => s.probes.notify);
  const [choice, setChoice] = useState<Choice>('phone');
  const narrow = useMediaQuery(NARROW_QUERY);
  const hasNotifications = SETTINGS_SECTIONS.some((s) => s.id === 'notifications');
  // A channel added here (push on this browser) shows in the live slice first: ask the hub again at once.
  const live = useNotifyState()?.channels.filter((c) => c.enabled).length ?? 0;
  useEffect(() => {
    if (live) void loadProbes();
  }, [live]);

  return (
    <div className="flex flex-col gap-4">
      <ChannelSummary notify={notify} />
      <Tabs value={choice} onValueChange={(v) => setChoice(v as Choice)}>
        <TabsList aria-label="Notification channel">
          {/* Short labels, and icons only where there is room, so the three tabs fit a 390 px phone. */}
          <TabsTrigger value="phone" icon={narrow ? undefined : <Smartphone />}>
            Phone
          </TabsTrigger>
          <TabsTrigger value="browser" icon={narrow ? undefined : <Monitor />}>
            Browser
          </TabsTrigger>
          <TabsTrigger value="team" icon={narrow ? undefined : <Mail />}>
            Slack and more
          </TabsTrigger>
        </TabsList>
        <TabsContent value="phone" className="flex flex-col gap-3 pt-4">
          <p className="flex items-center gap-2 text-caption text-fg-subtle">
            <Badge tone="accent">Recommended</Badge>
            Reaches you anywhere, with Approve and Deny on the lock screen.
          </p>
          <PhoneStep />
        </TabsContent>
        <TabsContent value="browser" className="flex flex-col gap-3 pt-4">
          <p className="text-caption text-fg-subtle">Push to this computer, even with the Orgus tab closed.</p>
          <PushControl />
        </TabsContent>
        <TabsContent value="team" className="flex flex-col gap-3 pt-4">
          <p className="text-ui text-fg-muted">An admin adds Slack, Teams and email in Settings, Notifications.</p>
          {hasNotifications && (
            <div>
              <Button size="sm" icon={<Settings />} onClick={() => navigate('/settings/notifications')}>
                Notification settings
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
